import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Plus, Edit, Trash2, Search, List, X, GripVertical, ChevronDown, ChevronRight, Image as ImageIcon } from 'lucide-react';
import { interestGroupAPI, interestsAPI } from '../api';
import { InterestGroup, Interest, CreateInterestGroupDto, ReorderGroupsDto } from '../types';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { PageHeader } from '../components/common/PageHeader';
import { LoadingState } from '../components/common/LoadingState';
import { ErrorState } from '../components/common/ErrorState';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { useNotification } from '../hooks/useNotification';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { SortableInterestItem } from '../components/SortableInterestItem';

// Type for populated interest within a group
interface PopulatedInterest {
  _id: string;
  name: string;
  icon_url?: string;
  image_url?: string;
  is_active?: boolean;
}

interface InterestGroupItem {
  interestId: PopulatedInterest | string;
  order: number;
}

// Sortable Group Item Component with Expanded Interests
const SortableGroupItem: React.FC<{
  group: InterestGroup;
  onEdit: (group: InterestGroup) => void;
  onDelete: (group: InterestGroup) => void;
  onManageInterests: (group: InterestGroup) => void;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onInterestsReorder: (groupId: string, interests: InterestGroupItem[]) => void;
}> = ({ group, onEdit, onDelete, onManageInterests, isExpanded, onToggleExpand, onInterestsReorder }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: group._id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const validInterests = (group.interests || []).filter(item => {
    if (typeof item.interestId === 'object' && item.interestId !== null) {
      return true;
    }
    return false;
  });

  const sortedInterests = [...validInterests].sort((a, b) => a.order - b.order);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleInterestDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = sortedInterests.findIndex(item => (item.interestId as PopulatedInterest)._id === active.id);
      const newIndex = sortedInterests.findIndex(item => (item.interestId as PopulatedInterest)._id === over?.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newOrder = arrayMove(sortedInterests, oldIndex, newIndex);
        
        // Update order values
        const updatedInterests = newOrder.map((item, index) => ({
          ...item,
          order: index
        }));
        
        onInterestsReorder(group._id, updatedInterests);
      }
    }
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Card className="mb-4">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div
                {...attributes}
                {...listeners}
                className="cursor-grab hover:cursor-grabbing"
              >
                <GripVertical className="h-5 w-5 text-gray-400" />
              </div>
              <Badge variant="secondary">{group.order + 1}</Badge>
              <div>
                <h3 className="font-semibold text-lg">{group.name}</h3>
                <p className="text-sm text-gray-500">{validInterests.length} interests</p>
              </div>
              <Badge variant={group.is_active ? "default" : "destructive"}>
                {group.is_active ? "Active" : "Inactive"}
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={onToggleExpand}
              >
                {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onManageInterests(group)}
              >
                <List className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onEdit(group)}
              >
                <Edit className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onDelete(group)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
          {isExpanded && (
            <div className="mt-4 pl-12">
              {sortedInterests.length > 0 ? (
                <div className="space-y-2">
                  <p className="text-sm text-gray-500 mb-2">Drag interests to reorder them within this group:</p>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleInterestDragEnd}
                  >
                    <SortableContext
                      items={sortedInterests.map(item => (item.interestId as PopulatedInterest)._id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="space-y-1">
                        {sortedInterests.map((item, index) => {
                          const interest = item.interestId as PopulatedInterest;
                          return (
                            <SortableInterestItem
                              key={interest._id}
                              interest={interest}
                              order={index}
                            />
                          );
                        })}
                      </div>
                    </SortableContext>
                  </DndContext>
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic">No interests in this group</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export const InterestGroupsPage: React.FC = () => {
  const [groups, setGroups] = useState<InterestGroup[]>([]);
  const [allInterests, setAllInterests] = useState<Interest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isManageInterestsOpen, setIsManageInterestsOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<InterestGroup | null>(null);
  const [formData, setFormData] = useState<CreateInterestGroupDto>({
    name: '',
    order: 0,
  });
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [selectedInterests, setSelectedInterests] = useState<string[]>([]);
  const [availableInterests, setAvailableInterests] = useState<Interest[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showNotification } = useNotification();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [groupsData, interestsData] = await Promise.all([
        interestGroupAPI.getAllGroups(),
        interestsAPI.getAllInterests(),
      ]);
      setGroups(groupsData);
      setAllInterests(interestsData);
      setError(null);
    } catch (err) {
      setError('Failed to fetch data');
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Sort and filter groups
  const sortedGroups = useMemo(() => {
    return [...groups].sort((a, b) => a.order - b.order);
  }, [groups]);

  const filteredGroups = useMemo(() => {
    if (!searchTerm) return sortedGroups;
    
    return sortedGroups.filter((group) =>
      group.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [sortedGroups, searchTerm]);

  // Handle create/edit dialog
  const handleOpenCreateDialog = () => {
    setFormData({
      name: '',
      order: groups.length,
    });
    setIsCreateDialogOpen(true);
  };

  const handleOpenEditDialog = (group: InterestGroup) => {
    setSelectedGroup(group);
    setFormData({
      name: group.name,
      order: group.order,
    });
    setIsEditDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      if (selectedGroup) {
        await interestGroupAPI.updateGroup(selectedGroup._id, formData);
        showNotification('Interest group updated successfully', 'success');
      } else {
        await interestGroupAPI.createGroup(formData);
        showNotification('Interest group created successfully', 'success');
      }
      await fetchData();
      setIsCreateDialogOpen(false);
      setIsEditDialogOpen(false);
      setSelectedGroup(null);
    } catch (err) {
      showNotification('Failed to save interest group', 'error');
      console.error('Error saving group:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle delete
  const handleDelete = (group: InterestGroup) => {
    setSelectedGroup(group);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedGroup) return;
    
    try {
      setIsSubmitting(true);
      await interestGroupAPI.deleteGroup(selectedGroup._id);
      showNotification('Interest group deleted successfully', 'success');
      await fetchData();
      setIsDeleteDialogOpen(false);
      setSelectedGroup(null);
    } catch (err) {
      showNotification('Failed to delete interest group', 'error');
      console.error('Error deleting group:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle drag and drop for groups
  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = sortedGroups.findIndex((g) => g._id === active.id);
      const newIndex = sortedGroups.findIndex((g) => g._id === over?.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newOrder = arrayMove(sortedGroups, oldIndex, newIndex);
        
        // Update local state optimistically
        setGroups(newOrder.map((group, index) => ({ ...group, order: index })));
        
        // Create reorder updates
        const updates: ReorderGroupsDto[] = newOrder.map((group, index) => ({
          groupId: group._id,
          order: index,
        }));

        try {
          await interestGroupAPI.reorderGroups(updates);
          showNotification('Groups reordered successfully', 'success');
        } catch {
          showNotification('Failed to reorder groups', 'error');
          await fetchData(); // Revert on error
        }
      }
    }
    setActiveId(null);
  };

  // Handle reorder interests within a group
  const handleInterestsReorder = async (groupId: string, updatedInterests: InterestGroupItem[]) => {
    try {
      // Prepare updates for API
      const updates = updatedInterests.map((item, index) => ({
        interestId: typeof item.interestId === 'object' ? item.interestId._id : item.interestId,
        order: index
      }));

      // Update local state optimistically
      setGroups(prevGroups => prevGroups.map(group => {
        if (group._id === groupId) {
          return {
            ...group,
            interests: updatedInterests
          };
        }
        return group;
      }));

      // Call API to persist the change
      await interestGroupAPI.reorderInterestsInGroup(groupId, updates);
      showNotification('Interests reordered successfully', 'success');
    } catch {
      showNotification('Failed to reorder interests', 'error');
      await fetchData(); // Revert on error
    }
  };

  // Toggle group expansion
  const toggleGroupExpand = (groupId: string) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  // Handle manage interests
  const handleManageInterests = (group: InterestGroup) => {
    setSelectedGroup(group);
    
    // Get current interest IDs in the group
    const currentInterestIds = (group.interests || [])
      .filter(item => typeof item.interestId === 'object' && item.interestId !== null)
      .map(item => (item.interestId as PopulatedInterest)._id);
    
    setSelectedInterests(currentInterestIds);
    
    // Filter out interests already in the group
    const available = allInterests.filter(
      interest => !currentInterestIds.includes(interest._id)
    );
    setAvailableInterests(available);
    
    setIsManageInterestsOpen(true);
  };

  const handleAddInterest = async (interestId: string) => {
    if (!selectedGroup) return;
    
    try {
      const nextOrder = (selectedGroup.interests || []).length;
      await interestGroupAPI.addInterestToGroup(selectedGroup._id, {
        interestId,
        order: nextOrder,
      });
      
      // Update local state
      setSelectedInterests([...selectedInterests, interestId]);
      setAvailableInterests(availableInterests.filter(i => i._id !== interestId));
      
      // Refresh data
      await fetchData();
      showNotification('Interest added to group', 'success');
    } catch (err) {
      showNotification('Failed to add interest', 'error');
      console.error('Error adding interest:', err);
    }
  };

  const handleRemoveInterest = async (interestId: string) => {
    if (!selectedGroup) return;
    
    try {
      await interestGroupAPI.removeInterestFromGroup(selectedGroup._id, interestId);
      
      // Update local state
      setSelectedInterests(selectedInterests.filter(id => id !== interestId));
      const interest = allInterests.find(i => i._id === interestId);
      if (interest) {
        setAvailableInterests([...availableInterests, interest]);
      }
      
      // Refresh data
      await fetchData();
      showNotification('Interest removed from group', 'success');
    } catch (err) {
      showNotification('Failed to remove interest', 'error');
      console.error('Error removing interest:', err);
    }
  };

  if (loading) return <DashboardLayout><LoadingState /></DashboardLayout>;
  if (error) return <DashboardLayout><ErrorState message={error} /></DashboardLayout>;

  return (
    <DashboardLayout>
      <PageHeader
        title="Interest Groups"
        description="Manage interest groups and their organization"
      />

      <div className="space-y-6">
        {/* Actions Bar */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div className="flex-1 max-w-md">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input
                    placeholder="Search groups..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <p className="text-sm text-gray-500">Drag and drop to reorder</p>
                <Button onClick={handleOpenCreateDialog}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add New Group
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Groups List */}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={filteredGroups.map(g => g._id)}
            strategy={verticalListSortingStrategy}
          >
            {filteredGroups.map((group) => (
              <SortableGroupItem
                key={group._id}
                group={group}
                onEdit={handleOpenEditDialog}
                onDelete={handleDelete}
                onManageInterests={handleManageInterests}
                isExpanded={expandedGroups.has(group._id)}
                onToggleExpand={() => toggleGroupExpand(group._id)}
                onInterestsReorder={handleInterestsReorder}
              />
            ))}
          </SortableContext>
          <DragOverlay>
            {activeId ? (
              <Card>
                <CardContent className="p-4">
                  <strong>
                    {groups.find(g => g._id === activeId)?.name}
                  </strong>
                </CardContent>
              </Card>
            ) : null}
          </DragOverlay>
        </DndContext>

        {filteredGroups.length === 0 && (
          <Card>
            <CardContent className="p-8 text-center">
              <p className="text-gray-500">
                {searchTerm
                  ? "No groups found matching your search."
                  : "No interest groups found. Create one to get started."}
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Create Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Interest Group</DialogTitle>
            <DialogDescription>
              Add a new interest group to organize interests
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Group Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter group name"
              />
            </div>
            <div>
              <Label htmlFor="order">Display Order</Label>
              <Input
                id="order"
                type="number"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                min={0}
              />
              <p className="text-sm text-gray-500 mt-1">
                Lower numbers appear first. You can also drag and drop to reorder.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? "Creating..." : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Interest Group</DialogTitle>
            <DialogDescription>
              Update the interest group details
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-name">Group Name</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter group name"
              />
            </div>
            <div>
              <Label htmlFor="edit-order">Display Order</Label>
              <Input
                id="edit-order"
                type="number"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                min={0}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the group "{selectedGroup?.name}"?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDelete} disabled={isSubmitting}>
              {isSubmitting ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage Interests Dialog */}
      <Dialog open={isManageInterestsOpen} onOpenChange={setIsManageInterestsOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Manage Interests in {selectedGroup?.name}</DialogTitle>
            <DialogDescription>
              Add or remove interests from this group. You can reorder them by expanding the group in the main view.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <h3 className="font-semibold mb-2">Available Interests</h3>
              <div className="border rounded-lg p-2 max-h-96 overflow-y-auto">
                {availableInterests.map((interest) => (
                  <div
                    key={interest._id}
                    className="flex items-center justify-between p-2 hover:bg-gray-50 rounded"
                  >
                    <div className="flex items-center gap-2">
                      {interest.icon_url ? (
                        <img
                          src={interest.icon_url}
                          alt={interest.name}
                          className="w-6 h-6 object-cover rounded"
                        />
                      ) : (
                        <div className="w-6 h-6 bg-gray-200 rounded flex items-center justify-center">
                          <ImageIcon className="h-3 w-3 text-gray-400" />
                        </div>
                      )}
                      <span className="text-sm">{interest.name}</span>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleAddInterest(interest._id)}
                    >
                      <Plus className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
                {availableInterests.length === 0 && (
                  <p className="text-sm text-gray-500 text-center p-4">
                    No available interests
                  </p>
                )}
              </div>
            </div>
            <div>
              <h3 className="font-semibold mb-2">Interests in Group</h3>
              <div className="border rounded-lg p-2 max-h-96 overflow-y-auto">
                {selectedGroup?.interests
                  .filter(item => typeof item.interestId === 'object' && item.interestId !== null)
                  .sort((a, b) => a.order - b.order)
                  .map((item) => {
                    const interest = item.interestId as PopulatedInterest;
                    return (
                      <div
                        key={interest._id}
                        className="flex items-center justify-between p-2 hover:bg-gray-50 rounded"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">{item.order + 1}.</span>
                          {interest.icon_url ? (
                            <img
                              src={interest.icon_url}
                              alt={interest.name}
                              className="w-6 h-6 object-cover rounded"
                            />
                          ) : (
                            <div className="w-6 h-6 bg-gray-200 rounded flex items-center justify-center">
                              <ImageIcon className="h-3 w-3 text-gray-400" />
                            </div>
                          )}
                          <span className="text-sm">{interest.name}</span>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRemoveInterest(interest._id)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    );
                  })}
                {(!selectedGroup?.interests || selectedGroup.interests.filter(item => typeof item.interestId === 'object').length === 0) && (
                  <p className="text-sm text-gray-500 text-center p-4">
                    No interests in this group
                  </p>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setIsManageInterestsOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};